import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';
import { pppoeApi } from '../api/pppoeApi';
import { ownerApi } from '../api/ownerApi'; 
import { adminApi } from '../api/adminApi'; // Add this import
import { useAuthStore } from '../store/authStore'; // Add this import
import { Modal } from './Modal';
import { AddIcon, EditIcon, DeleteIcon, BlockIcon } from './icons';

// --- NEW LOADING ANIMATION COMPONENTS ---
const Spinner = () => (
    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const LoadingDisplay = ({ message }) => (
    <div className="flex flex-col justify-center items-center p-8 text-center text-gray-500">
        <Spinner />
        <p className="mt-4 text-lg">{message}</p>
    </div>
);
// --- Main Component (Top Level Page) ---
// Define axiosInstance with interceptor, just like in App.js
const axiosInstance = axios.create({ baseURL: '/api' });
axiosInstance.interceptors.request.use((config) => {
    const token = useAuthStore.getState().token;
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
}, (error) => Promise.reject(error));
const pingerApi = {
    pingNow: async (data) => (await axiosInstance.post('/pinger/ping', data)).data,
};
export const PppoeManagementPage = () => {
    const { user } = useAuthStore();
    const [activeTab, setActiveTab] = useState('users');
    const [mikrotiks, setMikrotiks] = useState([]);
    const [selectedMikrotik, setSelectedMikrotik] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const isSetupTabEnabled = user?.role === 'admin' || user?.role === 'vendo-owner';

     useEffect(() => {
        const fetchDevicesAndPing = async () => {
            setIsLoading(true);
            try {
                const fetchCall = user.role === 'admin' ? adminApi.getAllMikrotiks : ownerApi.getMikrotiks;
                const initialDevices = await fetchCall();
                
                if (initialDevices && initialDevices.length > 0) {
                    const pingPromises = initialDevices.map(device => 
                        pingerApi.pingNow({ type: 'mikrotik', id: device.id })
                    );
                    const updatedDevices = await Promise.all(pingPromises);
                    setMikrotiks(updatedDevices);

                    // If a device was previously selected, find its updated version in the new list
                    // Otherwise, select the first device
                    const currentSelectedId = selectedMikrotik?.id;
                    const newSelectedDevice = updatedDevices.find(d => d.id === currentSelectedId) || updatedDevices[0];
                    setSelectedMikrotik(newSelectedDevice);
                } else {
                    setMikrotiks([]);
                    setSelectedMikrotik(null);
                }
            } catch (error) {
                toast.error("Could not load or update device list.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchDevicesAndPing();
        // The dependency array is [user.role] so this complex fetch only runs once on load or when the user role changes.
    }, [user.role]);

    const renderTabContent = () => {
        if (!selectedMikrotik) {
            return <p className="text-gray-500">Please select a device to manage.</p>;
        }
        
        const isDeviceOnline = selectedMikrotik?.isOnline;
        
        switch (activeTab) {
            case 'users': return <UserManagementTab device={selectedMikrotik} activeTab={activeTab} isDeviceOnline={isDeviceOnline} />;
            case 'active': return <ActiveSessionsTab device={selectedMikrotik} activeTab={activeTab} isDeviceOnline={isDeviceOnline} />;
            case 'setup':
                if (!isSetupTabEnabled) {
                    return (
                        <div className="p-4 text-center text-gray-600">
                            <p className="font-semibold text-lg mb-2">Setup Tab Temporarily Disabled</p>
                            <p>This feature is currently under maintenance. Please check back later.</p>
                        </div>
                    );
                }
                return <SetupTab device={selectedMikrotik} activeTab={activeTab} isDeviceOnline={isDeviceOnline} />;
            default: return null;
        }
    };

    if (isLoading) {
        return <LoadingDisplay message="Loading and checking device status..." />;
    }

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">PPPoE Management</h1>
                <div className="flex items-center">
                    <div className="flex flex-col">
                        <label htmlFor="device-select-pppoe" className="mb-1 text-sm font-medium text-gray-600">MikroTik Device</label>
                        <select
                            id="device-select-pppoe"
                            onChange={e => setSelectedMikrotik(mikrotiks.find(m => m.id.toString() === e.target.value))}
                            value={selectedMikrotik?.id || ''}
                            className="bg-gray-50 border border-gray-300 text-gray-900 rounded-md w-full sm:w-auto p-2"
                        >
                            <option value="">-- Select a Device --</option>
                            {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.deviceName}</option>)}
                        </select>
                    </div>
                     {selectedMikrotik && (
                        <span className={`px-3 py-1 ml-4 text-xs font-semibold rounded-full self-end mb-1 ${selectedMikrotik.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {selectedMikrotik.isOnline ? 'Online' : 'Offline'}
                        </span>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <div className="border-b border-gray-200 mb-4">
                    <nav className="flex space-x-4" aria-label="Tabs">
                        <button onClick={() => setActiveTab('users')} className={`px-3 py-2 font-medium text-sm rounded-t-md ${activeTab === 'users' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                            PPPoE Users
                        </button>
                        <button onClick={() => setActiveTab('active')} className={`px-3 py-2 font-medium text-sm rounded-t-md ${activeTab === 'active' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                            Active Sessions
                        </button>
                        <button
                            onClick={() => isSetupTabEnabled && setActiveTab('setup')}
                            disabled={!isSetupTabEnabled}
                            className={`px-3 py-2 font-medium text-sm rounded-t-md ${activeTab === 'setup' && isSetupTabEnabled ? 'bg-blue-100 text-blue-700' : 'text-gray-500'} ${!isSetupTabEnabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
                        >
                            Setup
                        </button>
                    </nav>
                </div>
                <div>{renderTabContent()}</div>
            </div>
        </div>
    );
};


// --- User Management Tab ---
const UserManagementTab = ({ device, activeTab, isDeviceOnline }) => {
    const [users, setUsers] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const { register, handleSubmit, reset } = useForm();

    const fetchUsersAndProfiles = useCallback(async () => {
        if (!device || !isDeviceOnline) {
            setIsLoading(false);
            setUsers([]);
            setProfiles([]);
            return;
        };
        setIsLoading(true);
        try {
            const [userData, configData] = await Promise.all([
                pppoeApi.getPppoeUsers(device.id),
                pppoeApi.getPppoeConfig(device.id),
            ]);
            setUsers(Array.isArray(userData) ? userData : []);
            setProfiles(configData && Array.isArray(configData.profiles) ? configData.profiles : []);
        } catch (error) {
            if (!error.message?.includes('offline')) {
                toast.error(error.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [device, isDeviceOnline]);

    useEffect(() => {
        if (activeTab === 'users') {
            fetchUsersAndProfiles();
        }
    }, [fetchUsersAndProfiles, activeTab]);
    
    const handleOpenModal = (user = null) => {
        setEditingUser(user);
        if (user) {
            reset({
                name: user.name,
                profile: user.profile,
                comment: user.comment,
                disabled: user.disabled,
            });
        } else {
            reset({
                name: '', password: '', profile: 'default-pppoe', comment: '', validUntil: '', disabled: false
            });
        }
        setModalOpen(true);
    };

    const onSubmit = async (data) => {
        let promise;
        if (editingUser) {
            const payload = {};
            if (data.name !== editingUser.name) payload.name = data.name;
            if (data.password) payload.password = data.password;
            if (data.profile !== editingUser.profile) payload.profile = data.profile;
            if (data.comment !== editingUser.comment) payload.comment = data.comment;
            if (data.disabled !== editingUser.disabled) payload.disabled = data.disabled;

            if (Object.keys(payload).length === 0) {
                toast("No changes were made.");
                setModalOpen(false);
                return;
            }
            promise = pppoeApi.updatePppoeUser(device.id, editingUser.id, payload);
        } else {
            promise = pppoeApi.createPppoeUser(device.id, data);
        }
        
        await toast.promise(promise, {
            loading: 'Saving user...',
            success: (res) => {
                setModalOpen(false);
                fetchUsersAndProfiles();
                return res.message;
            },
            error: (err) => err.response?.data?.message || err.message,
        });
    };
    
    const handleDelete = async (user) => {
        if (window.confirm(`Are you sure you want to delete user '${user.name}'?`)) {
            await toast.promise(pppoeApi.removePppoeUser(device.id, user.id), {
                loading: 'Deleting user...',
                success: 'User deleted!',
                error: (err) => err.message,
            });
            fetchUsersAndProfiles();
        }
    };
    
    return (
        <div>
            <div className="flex justify-end mb-4">
                <button 
                    onClick={() => handleOpenModal()} 
                    disabled={!isDeviceOnline}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
                    <AddIcon /> Add PPPoE User
                </button>
            </div>
             {isLoading ? <LoadingDisplay message="Loading users..." /> : !isDeviceOnline ? <p className="text-center text-gray-500 p-4">Device is offline. Cannot display users.</p> : (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiration</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comment</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                            {user.disabled ? 'Disabled' : 'Enabled'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{user.name}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{user.profile}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{user.expiration ? new Date(user.expiration).toLocaleString() : 'N/A'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{user.service}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{user.comment}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => handleOpenModal(user)} className="text-indigo-600 hover:text-indigo-900"><EditIcon /></button>
                                            <button onClick={() => handleDelete(user)} className="text-red-600 hover:text-red-900"><DeleteIcon /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} title={editingUser ? 'Edit PPPoE User' : 'Add PPPoE User'}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Username</label>
                        <input {...register('name', { required: true })} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Password</label>
                        <input type="password" {...register('password')} placeholder={editingUser ? "Leave blank to keep unchanged" : ""} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Profile</label>
                        <select {...register('profile')} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                            {profiles.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                        </select>
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-700">Comment</label>
                        <input {...register('comment')} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                    </div>
                    {!editingUser && (
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Expires On (Optional)</label>
                            <input type="datetime-local" {...register('validUntil')} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                        </div>
                    )}
                    {editingUser && (
                        <div className="flex items-center">
                            <input type="checkbox" {...register('disabled')} id="disabled-check" className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                            <label htmlFor="disabled-check" className="ml-2 block text-sm text-gray-900">Disable User</label>
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-4">
                         <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md">Cancel</button>
                         <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Save</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};


// --- Active Sessions Tab ---
const ActiveSessionsTab = ({ device, activeTab, isDeviceOnline }) => {
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchSessions = useCallback(async () => {
        if (!device) return;
        try {
            const data = await pppoeApi.getActiveSessions(device.id);
            setSessions(Array.isArray(data) ? data : []);
        } catch (error) {
            if (!error.message?.includes('offline')) {
                toast.error(error.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [device]);

    useEffect(() => {
        let interval;
        if (activeTab === 'active' && isDeviceOnline) {
            setIsLoading(true);
            fetchSessions();
            interval = setInterval(fetchSessions, 5000);
        } else {
            setSessions([]);
            setIsLoading(false);
        }
        return () => clearInterval(interval);
    }, [fetchSessions, activeTab, isDeviceOnline]);

    const handleDisconnect = async (session) => {
         if (window.confirm(`Are you sure you want to disconnect user '${session.name}'?`)) {
            await toast.promise(pppoeApi.removeActiveSession(device.id, session.id), {
                loading: 'Disconnecting session...',
                success: 'Session disconnected!',
                error: (err) => err.message,
            });
            fetchSessions();
        }
    }

    return (
        <div>
            {isLoading ? <p>Loading active sessions...</p> : !isDeviceOnline ? <p className="text-center text-gray-500 p-4">Device is offline. Cannot display active sessions.</p> : (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full">
                         <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uptime</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                         <tbody className="divide-y divide-gray-200">
                            {sessions.map(s => (
                                <tr key={s.id}>
                                    <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{s.name}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{s.service}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{s.address}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">{s.uptime}</td>
                                     <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                        <button onClick={() => handleDisconnect(s)} className="text-red-600 hover:text-red-900"><BlockIcon /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};


// --- Setup Tab ---
const SetupTab = ({ device, activeTab, isDeviceOnline }) => {
    
    const [config, setConfig] = useState({ pools: [], profiles: [], servers: [] });
    const [interfaces, setInterfaces] = useState([]);
    const [bridges, setBridges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    

    const { register: registerPool, handleSubmit: handlePoolSubmit, reset: resetPool } = useForm();
    const { register: registerProfile, handleSubmit: handleProfileSubmit, reset: resetProfile } = useForm();
    const { register: registerServer, handleSubmit: handleServerSubmit, reset: resetServer } = useForm();
    const { register: registerBridge, handleSubmit: handleBridgeSubmit, reset: resetBridge } = useForm();
    const { register: registerPort, handleSubmit: handlePortSubmit, reset: resetPort } = useForm();
    const { register: registerEdit, handleSubmit: handleEditSubmit, reset: resetEdit } = useForm();

    
    const fetchConfig = useCallback(async () => {
        if (!device || !isDeviceOnline) {
            setIsLoading(false);
            setConfig({ pools: [], profiles: [], servers: [] });
            setInterfaces([]);
            setBridges([]);
            return;
        };
        setIsLoading(true);
        try {
            const [configData, ifaceData, bridgeData] = await Promise.all([
                pppoeApi.getPppoeConfig(device.id),
                pppoeApi.getInterfaces(device.id),
                pppoeApi.getBridges(device.id)
            ]);
            setConfig(configData || { pools: [], profiles: [], servers: [] });
            setInterfaces(ifaceData || []);
            setBridges(bridgeData || []);
        } catch(e){ 
             if (!e.message?.includes('offline')) {
                toast.error(e.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [device, isDeviceOnline]);

    useEffect(() => { 
        if (activeTab === 'setup') {
            fetchConfig() 
        }
    }, [fetchConfig, activeTab]);

    const onPoolSubmit = async (data) => {
        await toast.promise(pppoeApi.createIpPool(device.id, data), {
            loading: 'Creating IP Pool...', success: (res) => { resetPool({name: '', ranges: ''}); fetchConfig(); return res.message }, error: (e) => e.message
        });
    };
    const onProfileSubmit = async (data) => {
         await toast.promise(pppoeApi.createPppProfile(device.id, data), {
            loading: 'Creating PPP Profile...', success: (res) => { resetProfile(); fetchConfig(); return res.message }, error: (e) => e.message
        });
    };
    const onServerSubmit = async (data) => {
         await toast.promise(pppoeApi.configurePppoeServer(device.id, data), {
            loading: 'Configuring PPPoE Server...', success: (res) => { resetServer(); fetchConfig(); return res.message }, error: (e) => e.message
        });
    };
    const onBridgeSubmit = async (data) => {
        await toast.promise(pppoeApi.createBridge(device.id, data), {
            loading: 'Creating Bridge...', success: (res) => { resetBridge({name: ''}); fetchConfig(); return res.message }, error: (e) => e.message
        });
    };
    const onPortSubmit = async (data) => {
        await toast.promise(pppoeApi.addPortToBridge(device.id, data), {
            loading: 'Adding Port to Bridge...', success: (res) => { resetPort(); fetchConfig(); return res.message }, error: (e) => e.message
        });
    };
    const handleDeleteBridge = async (bridge) => {
        if (window.confirm(`Are you sure you want to delete bridge '${bridge.name}'?`)) {
            await toast.promise(pppoeApi.removeBridge(device.id, bridge.id), {
                loading: 'Deleting Bridge...',
                success: (res) => { fetchConfig(); return res.message },
                error: (e) => e.message
            });
        }
    };
    
    const handleRemovePort = async (portName) => {
        if (window.confirm(`Are you sure you want to remove port '${portName}' from its bridge?`)) {
            await toast.promise(pppoeApi.removePortFromBridge(device.id, portName), {
                loading: `Removing port ${portName}...`,
                success: (res) => { fetchConfig(); return res.message },
                error: (e) => e.message
            });
        }
    };

    const handleOpenEditModal = (type, item) => {
        setEditingItem({ type, data: item });
        resetEdit({
            name: item.name || item.serviceName,
            ranges: item.ranges,
            localAddress: item.localAddress,
            remoteAddress: item.remoteAddress,
            rateLimit: item.rateLimit,
            dnsServer: item.dnsServer,
            interface: item.interface,
            defaultProfile: item.defaultProfile,
        });
        setEditModalOpen(true);
    };

    const onEditSubmit = async (formData) => {
        if (!editingItem) return;
        const { type, data: originalData } = editingItem;
        let promise;
        const payload = {};
        const originalName = originalData.name || originalData.serviceName;
        const newName = formData.name;

        if (String(newName).toLowerCase() !== String(originalName).toLowerCase()) {
            let nameExists = false;
            const newNameLower = String(newName).toLowerCase();
            if (type === 'IP Pool') {
                nameExists = config.pools.some(p => String(p.name).toLowerCase() === newNameLower && p.id !== originalData.id);
            } else if (type === 'PPP Profile') {
                nameExists = config.profiles.some(p => String(p.name).toLowerCase() === newNameLower && p.id !== originalData.id);
            } else if (type === 'PPPoE Server') {
                nameExists = config.servers.some(s => String(s.serviceName).toLowerCase() === newNameLower && s.id !== originalData.id);
            }
            if (nameExists) {
                toast.error(`Error: A ${type} with the name "${newName}" already exists.`);
                return;
            }
        }
        
        if (type === 'IP Pool') {
            if (formData.name !== originalData.name) payload.name = formData.name;
            if (formData.ranges !== originalData.ranges) payload.ranges = formData.ranges;
        } else if (type === 'PPP Profile') {
            if (formData.name !== originalData.name) payload.name = formData.name;
            if (formData.localAddress !== originalData.localAddress) payload.localAddress = formData.localAddress;
            if (formData.remoteAddress !== originalData.remoteAddress) payload.remoteAddress = formData.remoteAddress;
            if (formData.dnsServer !== originalData.dnsServer) payload.dnsServer = formData.dnsServer;
            if (formData.rateLimit !== originalData.rateLimit) payload.rateLimit = formData.rateLimit;
        } else if (type === 'PPPoE Server') {
            if (formData.name !== originalData.serviceName) payload.serviceName = formData.name;
            if (formData.interface !== originalData.interface) payload.interface = formData.interface;
            if (formData.defaultProfile !== originalData.defaultProfile) payload.defaultProfile = formData.defaultProfile;
        }

        if (Object.keys(payload).length === 0) {
            toast('No changes were made.');
            setEditModalOpen(false);
            return;
        }

        if (type === 'IP Pool') promise = pppoeApi.updateIpPool(device.id, originalData.id, payload);
        if (type === 'PPP Profile') promise = pppoeApi.updatePppProfile(device.id, originalData.id, payload);
        if (type === 'PPPoE Server') promise = pppoeApi.updatePppoeServer(device.id, originalData.id, payload);

        if(promise) {
            await toast.promise(promise, {
                loading: `Updating ${type}...`,
                success: (res) => { setEditModalOpen(false); fetchConfig(); return res.message; },
                error: (e) => e.response?.data?.message || e.message,
            });
        }
    };
    
    const handleDelete = async (type, item) => {
        const itemName = item.name || item.serviceName;
        if (window.confirm(`Are you sure you want to delete ${type} '${itemName}'?`)) {
            let promise;
            if (type === 'IP Pool') promise = pppoeApi.removeIpPool(device.id, item.id);
            if (type === 'PPP Profile') promise = pppoeApi.removePppProfile(device.id, item.id);
            if (type === 'PPPoE Server') promise = pppoeApi.removePppoeServer(device.id, item.id);

            if (promise) {
                await toast.promise(promise, {
                    loading: `Deleting ${type}...`,
                    success: (res) => { fetchConfig(); return res.message },
                    error: (e) => e.message
                });
            }
        }
    };
    
    if (isLoading) {
        return <p>Loading PPPoE configuration...</p>
    }
     if (!isDeviceOnline) {
        return <p className="text-center text-gray-500 p-4">Device is offline. Cannot display or manage setup.</p>;
    }
    
    const inputClass = "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
    const labelClass = "block text-sm font-medium text-gray-700";
    const buttonClass = "px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed";

    return (
        <div className="space-y-6">
            <details className="bg-white p-4 rounded-lg shadow-sm" open>
                <summary className="font-semibold cursor-pointer text-lg">IP Pools ({config.pools?.length || 0})</summary>
                <div className="mt-4 border-t pt-4">
                    <table className="min-w-full text-sm mb-4">
                        <thead className="bg-gray-50"><tr><th className="px-2 py-2 text-left font-medium text-gray-500">Name</th><th className="px-2 py-2 text-left font-medium text-gray-500">Ranges</th><th className="px-2 py-2 text-left font-medium text-gray-500">Actions</th></tr></thead>
                        <tbody className="divide-y">
                            {config.pools?.map(p => <tr key={p.id}><td className="px-2 py-2 font-medium">{p.name}</td><td className="px-2 py-2">{p.ranges}</td><td className="px-2 py-2 flex items-center gap-2"><button disabled={!isDeviceOnline} onClick={() => handleOpenEditModal('IP Pool', p)} className="text-indigo-500 hover:text-indigo-700 disabled:opacity-50"><EditIcon /></button><button disabled={!isDeviceOnline} onClick={() => handleDelete('IP Pool', p)} className="text-red-500 hover:text-red-700 disabled:opacity-50"><DeleteIcon /></button></td></tr>)}
                        </tbody>
                    </table>
                    <form onSubmit={handlePoolSubmit(onPoolSubmit)} className="flex items-center gap-2">
                        <input {...registerPool('name', {required: true})} placeholder="Pool Name (e.g., pppoe_pool)" className={inputClass} />
                        <input {...registerPool('ranges', {required: true})} placeholder="Address Ranges (e.g., 10.0.0.2-10.0.0.254)" className={inputClass} />
                        <button type="submit" disabled={!isDeviceOnline} className={buttonClass}>Add</button>
                    </form>
                </div>
            </details>

            <details className="bg-white p-4 rounded-lg shadow-sm" open>
                <summary className="font-semibold cursor-pointer text-lg">PPP Profiles ({config.profiles?.length || 0})</summary>
                <div className="mt-4 border-t pt-4">
                     <table className="min-w-full text-sm mb-4">
                        <thead className="bg-gray-50"><tr><th className="px-2 py-2 text-left font-medium text-gray-500">Name</th><th className="px-2 py-2 text-left font-medium text-gray-500">Remote Address</th><th className="px-2 py-2 text-left font-medium text-gray-500">Rate Limit</th><th className="px-2 py-2 text-left font-medium text-gray-500">Actions</th></tr></thead>
                        <tbody className="divide-y">
                            {config.profiles?.map(p => <tr key={p.id}><td className="px-2 py-2 font-medium">{p.name}</td><td className="px-2 py-2">{p.remoteAddress}</td><td className="px-2 py-2">{p.rateLimit}</td><td className="px-2 py-2 flex items-center gap-2"><button disabled={!isDeviceOnline} onClick={() => handleOpenEditModal('PPP Profile', p)} className="text-indigo-500 hover:text-indigo-700 disabled:opacity-50"><EditIcon /></button><button disabled={!isDeviceOnline} onClick={() => handleDelete('PPP Profile', p)} className="text-red-500 hover:text-red-700 disabled:opacity-50"><DeleteIcon /></button></td></tr>)}
                        </tbody>
                    </table>
                    <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                            <input {...registerProfile('name', {required: true})} placeholder="Profile Name" className={inputClass} />
                            <input {...registerProfile('localAddress')} placeholder="Local Address (e.g., Gateway)" className={inputClass} />
                             <select {...registerProfile('remoteAddress')} className={inputClass}>
                                <option value="">Select IP Pool</option>
                                {config.pools?.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                             </select>
                             <input {...registerProfile('dnsServer')} defaultValue="8.8.8.8" placeholder="DNS Server" className={inputClass} />
                             <input {...registerProfile('rateLimit')} placeholder="Rate Limit (e.g., 5M/5M)" className={inputClass} />
                        </div>
                        <button type="submit" disabled={!isDeviceOnline} className={`${buttonClass} w-full`}>Add PPP Profile</button>
                    </form>
                </div>
            </details>

            <details className="bg-white p-4 rounded-lg shadow-sm" open>
                <summary className="font-semibold cursor-pointer text-lg">PPPoE Servers ({config.servers?.length || 0})</summary>
                 <div className="mt-4 border-t pt-4">
                     <table className="min-w-full text-sm mb-4">
                        <thead className="bg-gray-50"><tr><th className="px-2 py-2 text-left font-medium text-gray-500">Service</th><th className="px-2 py-2 text-left font-medium text-gray-500">Interface</th><th className="px-2 py-2 text-left font-medium text-gray-500">Default Profile</th><th className="px-2 py-2 text-left font-medium text-gray-500">Actions</th></tr></thead>
                        <tbody className="divide-y">
                            {config.servers?.map(s => <tr key={s.id}><td className="px-2 py-2 font-medium">{s.serviceName}</td><td className="px-2 py-2">{s.interface}</td><td className="px-2 py-2">{s.defaultProfile}</td><td className="px-2 py-2 flex items-center gap-2"><button disabled={!isDeviceOnline} onClick={() => handleOpenEditModal('PPPoE Server', s)} className="text-indigo-500 hover:text-indigo-700 disabled:opacity-50"><EditIcon /></button><button disabled={!isDeviceOnline} onClick={() => handleDelete('PPPoE Server', s)} className="text-red-500 hover:text-red-700 disabled:opacity-50"><DeleteIcon /></button></td></tr>)}
                        </tbody>
                    </table>
                    <form onSubmit={handleServerSubmit(onServerSubmit)} className="flex items-center gap-2">
                        <input {...registerServer('serviceName')} placeholder="Service Name (e.g., MyNet)" className={`${inputClass} flex-1`} />
                        <select {...registerServer('interface', {required: true})} className={`${inputClass} flex-1`}>
                            <option value="">Select Interface</option>
                            {interfaces.map(i => <option key={i} value={i}>{i}</option>)}
                        </select>
                         <select {...registerServer('defaultProfile')} className={`${inputClass} flex-1`}>
                            <option value="">Select Default Profile</option>
                            {config.profiles?.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                        </select>
                        <button type="submit" disabled={!isDeviceOnline} className={buttonClass}>Add Server</button>
                    </form>
                </div>
            </details>

            <details className="bg-white p-4 rounded-lg shadow-sm" open>
                <summary className="font-semibold cursor-pointer text-lg">Bridge Management ({bridges?.length || 0})</summary>
                <div className="mt-4 border-t pt-4 space-y-4">
                    <div>
                        <h4 className="font-medium mb-2">Existing Bridges & Ports</h4>
                        {bridges.map(bridge => (
                            <div key={bridge.id} className="text-sm p-3 bg-gray-50 rounded mb-2">
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold">{bridge.name}</span>
                                    <div>
                                        <button disabled={!isDeviceOnline} onClick={() => handleDeleteBridge(bridge)} className="text-red-500 hover:text-red-700 disabled:opacity-50"><DeleteIcon /></button>
                                    </div>
                                </div>
                                <div className="pl-4 mt-2">
                                    {bridge.ports.length > 0 ? bridge.ports.map(port => (
                                        <div key={port} className="flex items-center justify-between">
                                            <span>- {port}</span>
                                            <button disabled={!isDeviceOnline} onClick={() => handleRemovePort(port)} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50">Remove</button>
                                        </div>
                                    )) : <p className="text-xs text-gray-400">No ports assigned</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <form onSubmit={handleBridgeSubmit(onBridgeSubmit)} className="space-y-2">
                        <label className={labelClass}>Create New Bridge</label>
                        <div className="flex items-center gap-2">
                            <input {...registerBridge('name', {required: true})} placeholder="New Bridge Name (e.g., bridge-pppoe)" className={`${inputClass} flex-1`} />
                            <button type="submit" disabled={!isDeviceOnline} className={buttonClass}>Create Bridge</button>
                        </div>
                    </form>

                    <form onSubmit={handlePortSubmit(onPortSubmit)} className="space-y-2">
                         <label className={labelClass}>Add Port to Bridge</label>
                        <div className="flex items-center gap-2">
                            <select {...registerPort('interface', {required: true})} className={`${inputClass} flex-1`}>
                                <option value="">Select Port to Add</option>
                                {interfaces.map(i => <option key={i} value={i}>{i}</option>)}
                            </select>
                            <select {...registerPort('bridge', {required: true})} className={`${inputClass} flex-1`}>
                                <option value="">Select Target Bridge</option>
                                {bridges.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                            </select>
                            <button type="submit" disabled={!isDeviceOnline} className={buttonClass}>Add Port</button>
                        </div>
                    </form>
                </div>
            </details>

            <Modal isOpen={isEditModalOpen} onClose={() => setEditModalOpen(false)} title={`Edit ${editingItem?.type}`}>
                <form onSubmit={handleEditSubmit(onEditSubmit)} className="space-y-4">
                    {editingItem?.type === 'IP Pool' && (
                        <>
                            <div><label className={labelClass}>Pool Name</label><input {...registerEdit('name', { required: true })} className={inputClass} /></div>
                            <div><label className={labelClass}>Address Ranges</label><input {...registerEdit('ranges', { required: true })} className={inputClass} /></div>
                        </>
                    )}
                    {editingItem?.type === 'PPP Profile' && (
                         <>
                            <div><label className={labelClass}>Profile Name</label><input {...registerEdit('name', { required: true })} className={inputClass} /></div>
                            <div><label className={labelClass}>Local Address</label><input {...registerEdit('localAddress')} className={inputClass} /></div>
                            <div><label className={labelClass}>Remote Address (Pool)</label><select {...registerEdit('remoteAddress')} className={inputClass}><option value="">Select IP Pool</option>{config.pools?.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}</select></div>
                            <div><label className={labelClass}>DNS Server</label><input {...registerEdit('dnsServer')} className={inputClass} /></div>
                            <div><label className={labelClass}>Rate Limit</label><input {...registerEdit('rateLimit')} className={inputClass} /></div>
                         </>
                    )}
                    {editingItem?.type === 'PPPoE Server' && (
                        <>
                            <div><label className={labelClass}>Service Name</label><input {...registerEdit('name', { required: true })} className={inputClass} /></div>
                            <div><label className={labelClass}>Interface</label><select {...registerEdit('interface')} className={inputClass}><option value="">Select Interface</option>{interfaces.map(i => <option key={i} value={i}>{i}</option>)}</select></div>
                            <div><label className={labelClass}>Default Profile</label><select {...registerEdit('defaultProfile')} className={inputClass}><option value="">Select Default Profile</option>{config.profiles?.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}</select></div>
                        </>
                    )}
                    <div className="flex justify-end gap-2 pt-4">
                        <button type="button" onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">Save Changes</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};